import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import type {
  PartnersLocationsQuery,
  PartnersLocationsQueryVariables,
} from 'graphql/research/types-and-hooks';
import {
  AllPartnersByLanguageDocument,
  AllPartnersByLanguageQuery,
  PartnersByScrappingIdsDocument,
  PartnersByScrappingIdsQuery,
} from 'graphql/wp/types-and-hooks';
import camelcaseKeys from 'camelcase-keys';
import { QueryError, QueryProps } from 'lib/api';
import type {
  GetLocationsNearbyVariables,
  LocationHoursWeek,
  LocationItem,
  LocationItems,
  QueryLocation,
} from 'lib/queries/locations';
import type { LanguageCodeFilterEnum } from './settings';
import {
  refetchPartnersLocationsQuery,
  PartnersNearbyDocument,
} from 'graphql/research/types-and-hooks';
import { getSettings } from './settings';
import { initializeWpApollo, queryResearch } from 'lib/apollo';
import {
  getLocationSchedules,
  getLocationsNearby,
} from 'lib/queries/locations';
import { snakeToCamel } from 'lib/utils/snakeToCamel';
import { Partner, PartnerDetails, Settings } from 'types';
import { request } from 'lib/rest';

const OPENING_HOURS_DEFAULT = JSON.stringify([
  {
    open: { day: '7', time: '08:00' },
    close: { day: '1', time: '20:00' },
  },
  {
    open: { day: '1', time: '08:00' },
    close: { day: '2', time: '20:00' },
  },
  {
    open: { day: '2', time: '08:00' },
    close: { day: '3', time: '20:00' },
  },
  {
    open: { day: '3', time: '08:00' },
    close: { day: '4', time: '20:00' },
  },
  {
    open: { day: '4', time: '08:00' },
    close: { day: '5', time: '20:00' },
  },
  {
    open: { day: '5', time: '08:00' },
    close: { day: '6', time: '20:00' },
  },
  {
    open: { day: '6', time: '08:00' },
    close: { day: '7', time: '20:00' },
  },
]);

export type AllPartnersLocations = {
  partners: AllPartnersByLanguageQuery['affiliates']['nodes'];
};

export async function getWpPartnersLocations({
  client,
  languageCode,
}: Pick<QueryProps, 'client'> & {
  languageCode: LanguageCodeFilterEnum;
}): Promise<AllPartnersLocations & QueryError> {
  const { data, error } = await client.query<AllPartnersByLanguageQuery>({
    query: AllPartnersByLanguageDocument,
    variables: { languageCode },
  });

  return {
    error,
    partners: data?.affiliates?.nodes || [],
  };
}

export type GetWpPartnersByScrappingIds = {
  partners: AllPartnersByLanguageQuery['affiliates']['nodes'];
};

export async function getWpPartnersByScrappingIds({
  client,
  scrappingIds,
}: Pick<QueryProps, 'client'> & {
  scrappingIds: string[];
}): Promise<AllPartnersLocations & QueryError> {
  const { data, error } = await client.query<PartnersByScrappingIdsQuery>({
    query: PartnersByScrappingIdsDocument,
    variables: { scrappingIds },
  });

  return {
    error,
    partners: data?.affiliates?.nodes || [],
  };
}

export type GetNearbyPartners = {
  partners?: LocationItems;
};

export async function getNearbyPartners({
  client,
  variables,
}: Pick<GetLocationsNearbyVariables, 'client' | 'variables'>): Promise<
  GetNearbyPartners & QueryError
> {
  const { data: nearbyGoogleLocations, error } = await getLocationsNearby({
    client,
    query: PartnersNearbyDocument,
    variables,
  });

  if (error) {
    return { error };
  }

  if (!nearbyGoogleLocations?.length) {
    return { partners: null };
  }

  const { partners: nearbyWpLocations } = await getWpPartnersByScrappingIds({
    client,
    scrappingIds: nearbyGoogleLocations.map(({ id }) => id.toString()),
  });

  return {
    error,
    partners: mapPartners({
      locations: nearbyGoogleLocations,
      wpData: nearbyWpLocations,
    }),
  };
}

export type PartnersLocationsResult = {
  locations: LocationItems;
};

export async function getPartnersLocations({
  client,
  languageCode,
}: Pick<QueryProps, 'client'> & {
  languageCode: LanguageCodeFilterEnum;
}): Promise<PartnersLocationsResult & QueryError> {
  const { data: researchPartners, error: researchPartnersError } =
    await queryResearch<
      PartnersLocationsQuery,
      PartnersLocationsQueryVariables
    >(client, refetchPartnersLocationsQuery());

  const { partners: wpData, error: wpPartnersError } =
    await getWpPartnersLocations({
      client,
      languageCode,
    });

  return {
    error: wpPartnersError || researchPartnersError,
    locations: mapPartners({
      locations: researchPartners?.nodes,
      wpData,
    }),
  };
}

export async function getSinglePartnerData({
  locale,
  slug,
  preview,
}: {
  locale: string;
  slug: string;
  preview: boolean;
}): Promise<Partner> {
  const { error, data } = await request<Partner>(
    `/affiliate?slug=${slug}&_fields=id,name,link,slug,seo,acf,details,taxonomy,translations,language${
      preview ? '&status=draft' : ''
    }`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw { error };
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export type SinglePartnerDetails = Omit<
  PartnerDetails,
  'openingHours' | 'states'
> & {
  openingHours: LocationHoursWeek;
  state: string;
};

export type SinglePartner = {
  partner: Omit<Partner, 'details'> & {
    details: SinglePartnerDetails;
  };
  nearbyLocations?: LocationItems;
  isStatic?: boolean;
  settings: Settings;
  isPreview: boolean;
};

export async function getSinglePartner({
  locale,
  params,
  preview,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<SinglePartner>> {
  try {
    const [singlePartner, settings] = await Promise.all([
      getSinglePartnerData({ locale, slug: params.slug as string, preview }),
      getSettings(locale),
    ]);

    if (!singlePartner || !settings) {
      return {
        notFound: true,
      };
    }

    const scrappingid = parseInt(singlePartner?.details?.leafwellScrappingId);
    const { details, ...partner } = singlePartner;
    const { openingHours, states, ...partnerDetails } = details;

    const researchData = {
      ...partner,
      details: {
        ...partnerDetails,
        openingHours: openingHours?.length
          ? getLocationSchedules(JSON.parse(openingHours))
          : getLocationSchedules(JSON.parse(OPENING_HOURS_DEFAULT)),
        state: states?.length ? states[0] : singlePartner.details.addressRegion,
      },
    };

    let nearbyLocations;

    if (scrappingid) {
      const client = initializeWpApollo();

      ({ partners: nearbyLocations } = await getNearbyPartners({
        client,
        variables: {
          excludeId: scrappingid,
          lat: Number(singlePartner.details.latitude),
          lng: Number(singlePartner.details.longitude),
        },
      }));
    }

    return {
      props: {
        settings: {
          ...settings,
          pageTranslations: singlePartner.translations || null,
        },
        nearbyLocations,
        partner: researchData,
        isStatic: true,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`error getting single partner ${params?.slug}:`, error);

    throw error;
  }
}

export function mapPartners({
  locations,
  wpData,
}: {
  locations: QueryLocation[];
  wpData: AllPartnersLocations['partners'];
}): LocationItems {
  return (
    locations?.map(location => {
      const wpItem = wpData?.find(
        ({ scrapping }) => parseInt(scrapping?.id) === location.id,
      );

      return mapPartner({ location, wpData: wpItem });
    }) || []
  );
}

export function mapPartner<
  WpData extends AllPartnersLocations['partners'][0],
  ReturnData extends LocationItem,
>({
  location,
  wpData,
}: {
  location: QueryLocation;
  wpData: WpData;
}): ReturnData {
  const {
    id,
    google_location: { address_components, opening_hours, ...googleLocation },
    ...rest
  } = location;

  const data = {
    ...snakeToCamel<ReturnData>({
      ...rest,
      ...googleLocation,
      reviews: googleLocation?.reviews
        ? JSON.parse(googleLocation?.reviews)
        : null,
    }),
    ...wpData,
    id,
    openingHours: opening_hours?.length
      ? getLocationSchedules(JSON.parse(opening_hours))
      : null,
    state: JSON.parse(address_components)?.find(({ types }) =>
      types.includes('administrative_area_level_1'),
    )?.short_name,
  };

  return data;
}
